import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  payloadString,
  payloadNumber,
} from '../realtime-sync.payload';
import { setProgressAnchor } from '@/shared/lib/progress';
import type { DataChangedEvent } from '../realtime-sync.types';
import { getAppRouter } from '@/shared/lib/app-router';

/**
 * Handle album started - create progress anchor for time-based progress.
 * This ensures UI shows smooth progress immediately, before queue snapshot arrives.
 */
export async function handleAlbumStarted(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  if (!payload || !event.id) return;

  const durationPredicted = payloadNumber(payload, 'duration_predicted');
  if (durationPredicted && durationPredicted > 0) {
    setProgressAnchor(event.id, {
      anchorUptime: performance.now(),
      anchorProgress: 0,
      durationPredicted,
    });
  }

  // Invalidate album queries
  const conceptId = payloadString(payload, 'concept_id');
  if (conceptId) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.albums.list(conceptId) });
  }
  const projectIdForAlbumStarted = payloadString(payload, 'project_id');
  if (projectIdForAlbumStarted) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAlbums.list(projectIdForAlbumStarted),
    });
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAlbums.batch(projectIdForAlbumStarted),
    });
  }
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}

export async function handleAlbumCompleted(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  const conceptId = payloadString(payload, 'concept_id');

  wsLogger.info('[RealtimeSync] Album completed:', {
    albumId: event.id,
    conceptId,
  });

  // Show success toast — body click navigates to the album (no inline button).
  // See render-completed handler above for the cross-platform rationale.
  // API-driven messages: skip toast if payload omits localized title.
  const projectIdForAlbumNav = payloadString(payload, 'project_id');
  {
    const title = payloadString(payload, 'title');
    if (title) {
      const description = payloadString(payload, 'message');
      const canNavigate = Boolean(projectIdForAlbumNav && conceptId);

      toast.completion(title, {
        ...(description && { description }),
        ...(canNavigate && {
          onTap: () => {
            getAppRouter()?.push(
              `/project/${projectIdForAlbumNav}/concept/${conceptId}?tab=album`
            );
          },
        }),
      });
    }
  }

  // Invalidate album queries
  if (conceptId) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.albums.list(conceptId) });
  }
  const projectIdForAlbumCompleted = payloadString(payload, 'project_id');
  if (projectIdForAlbumCompleted) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAlbums.list(projectIdForAlbumCompleted),
    });
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAlbums.batch(projectIdForAlbumCompleted),
    });
  }
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}

export async function handleAlbumFailed(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  const conceptId = payloadString(payload, 'concept_id');

  wsLogger.warn('[RealtimeSync] Album failed:', {
    albumId: event.id,
    error: payload?.error,
    conceptId,
  });

  // Show error toast — API-driven only.
  {
    const title = payloadString(payload, 'title');
    if (title) {
      const description =
        payloadString(payload, 'message') ?? payloadString(payload, 'error');
      toast.error(title, {
        ...(description && { description }),
        duration: 8000,
      });
    }
  }

  // Invalidate queries
  if (conceptId) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.albums.list(conceptId) });
  }
  const projectIdForAlbumFailed = payloadString(payload, 'project_id');
  if (projectIdForAlbumFailed) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAlbums.list(projectIdForAlbumFailed),
    });
    queryClient.invalidateQueries({
      queryKey: cacheKeys.projectAlbums.batch(projectIdForAlbumFailed),
    });
  }
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}
